/**
 * Idle Survey Modal Constants and Utility Functions
 * Shared utilities for idle survey detection and guards
 */

// Constants
export const IDLE_SURVEY_TIMEOUT_MS = 90_000;

// Ordered shortest-first so the first enabled flag wins
const TIMEOUT_FLAG_MAP: Array<{ flag: string; ms: number }> = [
  { flag: 'CSATSurveyTimeout15s', ms: 15_000 },
  { flag: 'CSATSurveyTimeout30s', ms: 30_000 },
  { flag: 'CSATSurveyTimeout60s', ms: 60_000 },
];

/**
 * If there are multiple flags enabled, returns the first match, and the array is ordered shortest-first. So, least time will be enabled.
 * If none are enabled, fall back to IDLE_SURVEY_TIMEOUT_MS.
 */
export const resolveIdleTimeout = (
  getFeature: Record<string, boolean> | undefined
): number => {
  if (!getFeature) return IDLE_SURVEY_TIMEOUT_MS;
  const match = TIMEOUT_FLAG_MAP.find((entry) => getFeature[entry.flag]);
  return match?.ms ?? IDLE_SURVEY_TIMEOUT_MS;
};
export const IDLE_SURVEY_DISMISSED_KEY = 'idleSurveyDismissed';
export const IDLE_SURVEY_BASE_URL =
  `${process.env.REACT_APP_PRESSGANEY_SURVEY_ID}?__digital_feedback_ctx=1`;
export const IDLE_SURVEY_ORIGIN = new URL(IDLE_SURVEY_BASE_URL).origin;
// Activity event types for idle detection
export const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'click',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
];

// Guard check utilities
/**
 * Check if any input, textarea, or select element is currently focused
 * @returns boolean - True if an input element is focused, false otherwise
 */
export const isInputFocused = (): boolean => {
  const el = document.activeElement;

  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
};

/**
 * Check if any modal element is currently displayed
 * @returns boolean - True if a blocking modal exists and is visible, false otherwise
 */
export const hasBlockingModal = (): boolean => {
  const modalElements = document.querySelectorAll(
    '.modal, .ReactModal__Body--open'
  );

  return Array.from(modalElements).some((element) => {
    const htmlElement = element as HTMLElement;
    const inlineDisplay = htmlElement.style?.display;
    const computedDisplay = globalThis.getComputedStyle(htmlElement).display;

    return inlineDisplay === 'block' || computedDisplay === 'block';
  });
};

/**
 * Check if the chatbot wrapper is visible
 * @returns boolean - True if chatbot wrapper exists and does not have d-none class, false otherwise
 */
export const isChatbotVisible = (): boolean => {
  const chatbotWrapper = document.querySelector(
    '.chatbot-wrapper.new-ai-chatbot-wrapper'
  );

  if (!chatbotWrapper) {
    return false;
  }

  return !chatbotWrapper.classList.contains('d-none');
};
//Check against cobrowse classname to see if cobrowse is active
export const isCoBrowseActive = (): boolean =>
  document.querySelector('.cobrowse-indicator-banner') !== null;

// ── Survey param code mappings ───────

const LOB_CODES: Record<string, string> = {
  medicare: '1',
  medicaid: '2',
  commercial: '3',
  individual: '4',
  dsnp: '5',
};

export const mapLobCode = (lob: string | undefined): string => {
  const key = (lob ?? '').toLowerCase().replaceAll(/[^a-z]/g, '');
  return LOB_CODES[key] ?? '';
};

export const mapDeviceTypeCode = (deviceType: string): string => {
  if (deviceType === 'mobile') return '1';
  if (deviceType === 'tablet') return '3';
  return '2';
};

export const mapCarrierCode = (avMed: boolean): string => (avMed ? '2' : '1');

// Pages where the idle survey popup should never appear
const EXCLUDED_PATHS: string[] = [
  '/home/find-a-doctor',
  '/home/change-primary-care-provider',
  '/home/pharmacy-claims',
  '/home/pay-monthly-premium',
  '/home/pay-your-sentara-bill',
];

export const isExcludedPage = (pathname: string): boolean =>
  EXCLUDED_PATHS.some((p) => pathname.startsWith(p));
